import {
  BTW_MULTIPLIER_INCL,
  calculateConfiguratorPrice,
  calcKozijnenAreaSqm,
  KOZIJNEN_BASE_PRICE_INCL_VAT,
  KOZIJNEN_AREA_INCREMENT_SQM,
  KOZIJNEN_AREA_INCREMENT_PRICE_INCL_VAT,
  KOZIJNEN_MIN_DIMENSION_MM,
  calcKozijnenVentilatorTotalInclVat,
  calcKozijnenOverheadTotalInclVat,
  calcKozijnenUnderlightTotalInclVat,
  calcKozijnenVensterbankTotalInclVat,
  calcKozijnenOpeningMechanismTotalInclVat,
  calcKozijnenHorTotalInclVat,
  calcGlazing36Premium,
  normalizeDoorCatalogPriceToExVat,
  getVoordeurPriceBreakdown,
  getAchterdeurPriceBreakdown,
  getTerrasdeurPriceBreakdown,
  calcSchuifpuiVentilatorTotal,
  SCHUIFPUI_SLIDING_BASE_PRICES,
  calcSchuifpuiDimensionSurcharge,
} from './pdfPricing';
import { detectPageType } from './vmkQuoteTable';
import { isGlazing36mm } from './vmkGlazing';

const round2 = (value) => Math.round(Number(value) * 100) / 100;

const inclToEx = (amountInclVat) => normalizeDoorCatalogPriceToExVat(amountInclVat);

const emptyBuckets = () => ({
  frames: 0,
  glass: 0,
  hardware: 0,
  install: 0,
  finish: 0,
});

const sumBuckets = (buckets) =>
  Object.values(buckets).reduce((total, value) => total + value, 0);

const scaleBuckets = (buckets, targetTotal) => {
  const current = sumBuckets(buckets);
  if (current <= 0 || targetTotal <= 0) {
    return emptyBuckets();
  }
  const scale = targetTotal / current;
  return Object.fromEntries(
    Object.entries(buckets).map(([key, value]) => [key, round2(value * scale)])
  );
};

const addDoorBreakdownLines = (buckets, lines) => {
  lines.forEach(({ key, amount }) => {
    const value = inclToEx(amount);
    if (key === 'openingWindow') {
      buckets.glass += value;
      return;
    }
    if (key === 'hvl' || key === 'twoParts') {
      buckets.finish += value;
      return;
    }
    buckets.frames += value;
  });
};

const estimateKozijnenBuckets = (config) => {
  const buckets = emptyBuckets();
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width < KOZIJNEN_MIN_DIMENSION_MM || height < KOZIJNEN_MIN_DIMENSION_MM) {
    return buckets;
  }

  const areaSqm = calcKozijnenAreaSqm(width, height);
  const extraArea = Math.max(0, areaSqm - 1);
  const areaPriceIncl = round2(
    KOZIJNEN_BASE_PRICE_INCL_VAT +
      (extraArea / KOZIJNEN_AREA_INCREMENT_SQM) * KOZIJNEN_AREA_INCREMENT_PRICE_INCL_VAT
  );

  buckets.frames += inclToEx(areaPriceIncl);
  buckets.finish += inclToEx(calcKozijnenVentilatorTotalInclVat(config));
  buckets.finish += inclToEx(calcKozijnenOverheadTotalInclVat(config));
  buckets.finish += inclToEx(calcKozijnenUnderlightTotalInclVat(config));
  buckets.finish += inclToEx(calcKozijnenVensterbankTotalInclVat(config));
  buckets.hardware += inclToEx(calcKozijnenOpeningMechanismTotalInclVat(config));
  buckets.hardware += inclToEx(calcKozijnenHorTotalInclVat(config));
  buckets.glass += calcGlazing36Premium(config, 'kozijnen');

  if (!isGlazing36mm(config.glazingType) && buckets.frames > 0) {
    const glassShare = 0.18;
    const glassPortion = round2(buckets.frames * glassShare);
    buckets.frames -= glassPortion;
    buckets.glass += glassPortion;
  }

  return buckets;
};

const estimateQuoteBaseBuckets = (quote) => {
  const config = quote?.config || {};
  const pageType = quote?.type || detectPageType(config);
  const buckets = emptyBuckets();

  if (pageType === 'kozijnen' || pageType === undefined) {
    return estimateKozijnenBuckets(config);
  }

  if (pageType === 'voordeur') {
    addDoorBreakdownLines(buckets, getVoordeurPriceBreakdown(config));
    buckets.glass += calcGlazing36Premium(config, pageType);
    return buckets;
  }

  if (pageType === 'achterdeur') {
    addDoorBreakdownLines(buckets, getAchterdeurPriceBreakdown(config));
    buckets.glass += calcGlazing36Premium(config, pageType);
    return buckets;
  }

  if (pageType === 'terrasdeur') {
    addDoorBreakdownLines(buckets, getTerrasdeurPriceBreakdown(config));
    buckets.glass += calcGlazing36Premium(config, pageType);
    return buckets;
  }

  if (pageType === 'schuifpui') {
    const width = Number(config.width) || 0;
    const height = Number(config.height) || 0;
    const position = config.slidingPosition || 'left';
    const baseIncl = SCHUIFPUI_SLIDING_BASE_PRICES[position] || 0;
    const dimensionIncl = calcSchuifpuiDimensionSurcharge(width, height);
    buckets.frames += inclToEx(baseIncl + dimensionIncl);
    buckets.finish += inclToEx(calcSchuifpuiVentilatorTotal(config));
    buckets.glass += calcGlazing36Premium(config, pageType);
    return buckets;
  }

  if (pageType === 'keralit') {
    const { price, hasPricing } = calculateConfiguratorPrice(config, pageType);
    if (hasPricing) {
      buckets.frames += price;
    }
    return buckets;
  }

  const { price, hasPricing } = calculateConfiguratorPrice(config, pageType);
  if (hasPricing) {
    buckets.frames += price;
  }
  return buckets;
};

const allocateQuoteLineBuckets = (quote, getQuoteLineTotal) => {
  const quantity = quote?.config?.quantity || 1;
  const lineExVat = getQuoteLineTotal(quote);
  const baseBuckets = estimateQuoteBaseBuckets(quote);

  Object.keys(baseBuckets).forEach((key) => {
    baseBuckets[key] = round2(baseBuckets[key] * quantity);
  });

  const baseTotal = sumBuckets(baseBuckets);
  const remainder = round2(lineExVat - baseTotal);

  if (remainder > 0) {
    baseBuckets.install += round2(remainder * 0.55);
    baseBuckets.finish += round2(remainder * 0.45);
  }

  if (sumBuckets(baseBuckets) <= 0) {
    return scaleBuckets(
      { frames: 0.5, glass: 0.15, hardware: 0.1, install: 0.15, finish: 0.1 },
      lineExVat
    );
  }

  if (Math.abs(sumBuckets(baseBuckets) - lineExVat) <= 0.02) {
    return baseBuckets;
  }

  return scaleBuckets(baseBuckets, lineExVat);
};

export const buildVmkInvestmentBreakdown = ({
  savedQuotes = [],
  getQuoteLineTotal,
  totalPriceExVat = 0,
}) => {
  const totals = emptyBuckets();

  savedQuotes.forEach((quote) => {
    const quoteBuckets = allocateQuoteLineBuckets(quote, getQuoteLineTotal);
    Object.keys(totals).forEach((key) => {
      totals[key] += quoteBuckets[key];
    });
  });

  const totalExVat = round2(totalPriceExVat);
  const totalInclVat = round2(totalExVat * BTW_MULTIPLIER_INCL);

  if (sumBuckets(totals) <= 0 && totalExVat > 0) {
    const fallback = scaleBuckets(
      { frames: 0.66, glass: 0.14, hardware: 0.06, install: 0.08, finish: 0.06 },
      totalExVat
    );
    Object.assign(totals, fallback);
  } else if (Math.abs(sumBuckets(totals) - totalExVat) > 0.05) {
    Object.assign(totals, scaleBuckets(totals, totalExVat));
  }

  const rowKeys = ['frames', 'glass', 'hardware', 'install', 'finish'];
  const rowsExVat = rowKeys.map((key) => round2(totals[key]));
  const rowsSumEx = round2(rowsExVat.reduce((sum, value) => sum + value, 0));
  const roundingDeltaEx = round2(totalExVat - rowsSumEx);
  if (Math.abs(roundingDeltaEx) >= 0.01) {
    const framesIndex = rowKeys.indexOf('frames');
    rowsExVat[framesIndex] = round2(rowsExVat[framesIndex] + roundingDeltaEx);
  }

  const rowsInclVat = rowsExVat.map((amount) => round2(amount * BTW_MULTIPLIER_INCL));
  const rowsSumIncl = round2(rowsInclVat.reduce((sum, value) => sum + value, 0));
  const roundingDeltaIncl = round2(totalInclVat - rowsSumIncl);
  if (Math.abs(roundingDeltaIncl) >= 0.01) {
    rowsInclVat[0] = round2(rowsInclVat[0] + roundingDeltaIncl);
  }

  return {
    totalExVat,
    totalInclVat,
    rowsInclVat,
    rowKeys,
  };
};
